import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LakshLogo } from '@/components/LakshLogo';
import axios from 'axios';
import { FeedbackDialog } from '@/components/FeedbackDialog';
import {
  ArrowLeft,
  Users,
  LayoutDashboard,
  Calendar,
  IndianRupee,
  Briefcase,
  LogOut,
  FileText,
  Receipt,
  Building2,
  TrendingUp
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (num) => {
  if (!num) return '₹0';
  if (num >= 10000000) return `₹${(num/10000000).toFixed(2)} Cr`;
  if (num >= 100000) return `₹${(num/100000).toFixed(2)} L`;
  return `₹${num.toLocaleString('en-IN')}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-IN');
};

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/ca/dashboard' },
  { icon: Users, label: 'Clients', path: '/ca/clients' },
  { icon: Calendar, label: 'Compliance', path: '/ca/compliance' },
  { icon: IndianRupee, label: 'Revenue', path: '/ca/revenue' },
];

export default function CAClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [firm, setFirm] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('laksh_ca_token');
    const userData = localStorage.getItem('laksh_ca_user');
    const firmData = localStorage.getItem('laksh_ca_firm');
    
    if (!token) {
      navigate('/ca/login');
      return;
    }
    
    if (userData) setUser(JSON.parse(userData));
    if (firmData) setFirm(JSON.parse(firmData));
    
    fetchClientDetails(token);
  }, [id, navigate]);

  const fetchClientDetails = async (token) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/ca/client/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Client details error:', error);
      if (error.response?.status === 404) {
        navigate('/ca/clients');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('laksh_ca_token');
    localStorage.removeItem('laksh_ca_user');
    localStorage.removeItem('laksh_ca_firm');
    navigate('/ca/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#06100A] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#1D9E75] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const { client, business, invoices, expenses } = data || {};

  return (
    <div className="min-h-screen bg-[#06100A]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-[#0a1a12] border-r border-white/10 flex flex-col">
        <div className="p-4 border-b border-white/10">
          <LakshLogo height={32} />
          <div className="mt-2 flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-[#1D9E75]" />
            <span className="text-xs text-[#1D9E75] font-medium">CA Portal</span>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              data-testid={`ca-nav-${item.label.toLowerCase()}`}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-md text-gray-400 transition-colors hover:bg-white/5 hover:text-white ${
                item.path === '/ca/clients' ? 'bg-[#1D9E75]/20 text-[#1D9E75]' : ''
              }`}
            >
              <item.icon className="w-5 h-5" strokeWidth={1.5} />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#1D9E75]/20 rounded-full flex items-center justify-center">
              <span className="text-[#1D9E75] font-semibold">{user?.name?.charAt(0) || 'C'}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-gray-500 truncate">{firm?.name}</p>
            </div>
          </div>
          <FeedbackDialog portal="ca" token={localStorage.getItem('laksh_ca_token')} triggerClassName="w-full px-4 py-2 mb-2 rounded-md text-gray-400 hover:text-white hover:bg-white/5" label="Send feedback" />
          <Button data-testid="ca-logout-btn" variant="ghost" onClick={handleLogout} className="w-full text-gray-400 hover:text-white hover:bg-white/5">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            <Link to="/ca/clients" data-testid="ca-client-back-link">
              <Button variant="ghost" className="text-gray-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-white font-['Outfit']">{business?.name}</h1>
              <p className="text-gray-400" data-testid="ca-client-contact">
                {client?.client_name}{client?.client_email ? ` · ${client.client_email}` : ''}
                {business?.gstin && <span className="font-mono ml-2">{business.gstin}</span>}
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-[#0a1a12] border-white/10">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#1D9E75]/20 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-[#1D9E75]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white font-mono">{data?.total_invoices || 0}</p>
                    <p className="text-xs text-gray-400">Total Invoices</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#0a1a12] border-white/10">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#EF9F27]/20 rounded-lg flex items-center justify-center">
                    <Receipt className="w-5 h-5 text-[#EF9F27]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white font-mono">{data?.total_expenses || 0}</p>
                    <p className="text-xs text-gray-400">Total Expenses</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#0a1a12] border-white/10">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white font-mono">{data?.customer_count || 0}</p>
                    <p className="text-xs text-gray-400">Customers</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#0a1a12] border-white/10">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#EF9F27]/20 rounded-lg flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-[#EF9F27]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white font-mono">{data?.vendor_count || 0}</p>
                    <p className="text-xs text-gray-400">Vendors</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Invoices */}
            <Card className="bg-[#0a1a12] border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#1D9E75]" />
                  Recent Invoices
                </CardTitle>
              </CardHeader>
              <CardContent>
                {invoices?.length > 0 ? (
                  <div className="space-y-3">
                    {invoices.map((inv) => (
                      <div key={inv.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                        <div>
                          <p className="text-white font-medium">{inv.customer_name}</p>
                          <p className="text-xs text-gray-500">{inv.reference_number}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-white font-mono">{formatCurrency(inv.amount + inv.tax_amount)}</p>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            inv.status === 'paid' ? 'bg-green-500/20 text-green-400' :
                            inv.status === 'overdue' ? 'bg-red-500/20 text-red-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {inv.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No invoices yet</p>
                )}
              </CardContent>
            </Card>

            {/* Recent Expenses */}
            <Card className="bg-[#0a1a12] border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-[#EF9F27]" />
                  Recent Expenses
                </CardTitle>
              </CardHeader>
              <CardContent>
                {expenses?.length > 0 ? (
                  <div className="space-y-3">
                    {expenses.map((exp) => (
                      <div key={exp.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                        <div>
                          <p className="text-white font-medium">{exp.description}</p>
                          <p className="text-xs text-gray-500">{formatDate(exp.transaction_date)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-white font-mono">{formatCurrency(exp.amount + exp.tax_amount)}</p>
                          {exp.itc_eligible && (
                            <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">
                              ITC
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No expenses yet</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Business Details */}
          <Card className="bg-[#0a1a12] border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Business Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Plan</p>
                  <p className="text-white capitalize">{business?.plan || 'Free'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Revenue Band</p>
                  <p className="text-white">{business?.revenue_band?.replace('_', ' ') || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">GST Scheme</p>
                  <p className="text-white capitalize">{business?.gst_scheme || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">State</p>
                  <p className="text-white">{business?.state_code || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
